<template>
    <div class="container-fluid">
        <h1>{{ title }}</h1>
        <br />
        <b-form
            ref="userform"
            name="userForm"
            v-on:submit.prevent="$_onSubmit"
            class="col-xs-8 col-md-5 pb-5"
        >
            <div class="form-group">
                <!-- Name -->
                <x-input
                    :id="`UserGroupName`"
                    :name="`UserGroupName`"
                    :text="$t('labelUserGroupname')"
                    maxlength="40"
                    :state="nameInvalidMessage == null"
                    v-model="record.name"
                    @input="$_checkName()"
                    :invalidMessage="nameInvalidMessage"
                    autofocus
                >
                </x-input>
            </div>
            <!-- Ondergrondlaag profiel -->
            <div class="form-group" v-if="userInfo.licenses.has_mobile_license">
                <x-select
                    :id="`slcBaseLayer`"
                    :name="`slcBaseLayer`"
                    :text="$t('labelProfileBaseLayer')"
                    :options="
                        $_convertToValueTextPair(
                            this.profilesType1,
                            'id',
                            'name'
                        )
                    "
                    required
                    :state="null"
                    :value="selectedBaseLayerId"
                    @selected="$_onBaseLayerSelectSelected"
                    :simpleMode="true"
                >
                </x-select>
            </div>
            <!-- Theme and storage layer profiles -->
            <div class="form-group" v-if="userInfo.licenses.has_mobile_license">
                <label class="control-label" for="ondergrond_profile">{{
                    $t('labelProfilesTheme')
                }}</label>
                <b-table
                    thead-class="d-none"
                    striped
                    hover
                    small
                    outlined
                    bordered
                    :tbody-tr-class="$_rowClass"
                    :items="profilesType2"
                    :fields="fields"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                    @row-clicked="$_toggleUserEnabled"
                >
                    <template v-slot:cell(userEnabled)="row">
                        <b-form-checkbox
                            v-bind:id="`${row.item.id}userEnabled`"
                            v-bind:name="`${row.item.id}userEnabled`"
                            v-model="row.item.userEnabled"
                        >
                        </b-form-checkbox>
                    </template>
                </b-table>
            </div>
            <!-- API profiles -->
            <div
                class="form-group"
                v-if="userInfo.licenses.has_mobile_license && hasAnyApiLicense"
            >
                <label class="control-label">{{
                    $t('labelProfileNameApi')
                }}</label>
                <b-table
                    thead-class="d-none"
                    striped
                    hover
                    small
                    outlined
                    bordered
                    :tbody-tr-class="$_rowClass"
                    :items="apiProfiles"
                    :fields="fields"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                    @row-clicked="$_toggleUserEnabled"
                >
                    <template v-slot:cell(userEnabled)="row">
                        <b-form-checkbox
                            v-bind:id="`${row.item.id}userEnabled`"
                            v-bind:name="`${row.item.id}userEnabled`"
                            v-model="row.item.userEnabled"
                        >
                        </b-form-checkbox>
                    </template>
                </b-table>
            </div>
            <!-- Save/cancel/remove buttons -->
            <SubmitCancelRemove
                :is-saving="isSaving"
                :is-deleting="isDeleting"
                :mode="editMode ? modeEditConst : modeAddConst"
                @cancel="$_onCancel"
                @remove="$_onRemove"
                :isBtnSaveDisabled="isBtnSaveDisabled"
            >
            </SubmitCancelRemove>
        </b-form>
    </div>
</template>

<script>
    import { convertToValueTextPair } from '../../services/utilities';
    import { MODE_ADD, MODE_EDIT, PROFILE_TYPE } from '../../constants';
    import XInput from '../../components/BaseComponents/x-input.vue';
    import { orderBy } from 'lodash';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import SubmitCancelRemove from '../../components/SubmitCancelRemove.vue';
    import XSelect from '../../components/BaseComponents/x-select.vue';
    import { mapState } from 'vuex';

    export default {
        mixins: [modalMixin],

        components: {
            SubmitCancelRemove,
            'x-select': XSelect,
            'x-input': XInput
        },

        props: { id: { type: Number, required: false, default: 0 } },

        data() {
            return {
                // Constants
                modeAddConst: MODE_ADD,
                modeEditConst: MODE_EDIT,

                userInfo: {},
                record: {},

                sortBy: 'name',
                sortDesc: false,

                fields: [
                    { key: 'name' },
                    {
                        key: 'userEnabled',
                        tdClass: 'user-enabled'
                    }
                ],

                organizations: [],
                profilesType1: [],
                profilesType2: [],
                apiProfiles: [],
                userGroups: [],

                selectedOrgId: null,
                selectedBaseLayerId: null,

                editMode: false,

                isSaving: false,
                isDeleting: false,
                nameInvalidMessage: null
            };
        },

        computed: {
            ...mapState(['hasAnyApiLicense']),

            /**
             * Check if user can be saved
             * @returns {boolean} indicating if user can be saved
             */
            isBtnSaveDisabled() {
                // No invalid name message
                if (
                    !(
                        this.nameInvalidMessage === null ||
                        this.nameInvalidMessage === ''
                    )
                ) {
                    return true;
                }

                // When saving or deleting
                if (this.isSaving || this.isDeleting) {
                    return true;
                }

                // Disable when name is empty
                return !this.record.name;
            }
        },

        created() {
            this.userInfo = this.$store.getters.userInfo;

            if (this.id && this.id > 0) {
                this.editMode = true;
                this.title = this.$t('userGroupDetailEditTitle');
            } else {
                this.editMode = false;
                this.title = this.$t('userGroupDetailAddTitle');
            }
        },

        mounted() {
            // Retrieve organizations

            if (this.editMode) {
                this.$_getUserGroup();
            } else {
                // Retrieve all profiles
                if (this.userInfo.licenses.has_mobile_license) {
                    this.$_getAllProfiles();
                }

                // Default settings for new user
                this.record = { id: 0 };
            }
        },

        async beforeCreate() {
            // get all data from the backend
            const result = await this.$store.dispatch('useApiData', {
                name: 'GetUserGroups'
            });

            this.userGroups = result.data;
        },

        methods: {
            $_convertToValueTextPair(inputArray, key, value) {
                return convertToValueTextPair(inputArray, key, value);
            },

            $_rowClass() {
                return 'tablerow';
            },

            $_checkName() {
                let inUse;
                if (this.userGroups) {
                    inUse = this.userGroups.find(
                        (ug) =>
                            ug.name.toLowerCase() === this.record.name.toLowerCase()
                    );
                }

                if (inUse && inUse.id != this.record.id) {
                    this.nameInvalidMessage = this.$t('errorUserGroupExists');
                } else {
                    this.nameInvalidMessage = null;
                }
            },

            async $_getUserGroup() {
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetUserGroup',
                    parameters: { Id: this.id }
                });

                this.record = { ...result.data };
                delete this.record.organizationId;
                delete this.record.userGroupUsers;
                if (this.userInfo.licenses.has_mobile_license) {
                    this.$_getAllDetailProfiles();
                }
            },

            async $_getAllProfiles() {
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetAllProfiles'
                });
                this.$_copyProfiles(result?.data);
            },

            async $_getAllDetailProfiles() {
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetAllDetailProfiles'
                });
                const profiles = result?.data;
                // Set flag for user=>profile relations
                for (let p of profiles) {
                    if (this.record.userGroupProfiles) {
                        for (let ugp of this.record.userGroupProfiles) {
                            if (ugp.profileId === p.id) {
                                p.userEnabled = true;
                                if (p.profileTypeId === 1) {
                                    this.selectedBaseLayerId = p.id;
                                }
                                break;
                            }
                        }
                    }
                }
                this.$_copyProfiles(profiles);
            },

            $_copyProfiles(profiles) {
                // Copy profiles to this.profilesType1
                this.profilesType1 = profiles.filter(function (elem) {
                    return elem.profileTypeId === PROFILE_TYPE.BASELAYER;
                });

                // Copy profiles to this.profilesType2
                this.profilesType2 = profiles.filter(function (elem) {
                    return elem.profileTypeId === PROFILE_TYPE.THEME_FEEDBACK;
                });

                // Copy profiles of type API
                this.apiProfiles = profiles.filter(function (elem) {
                    return elem.profileTypeId === PROFILE_TYPE.API;
                });

                // Sort records (lodash)
                this.profilesType1 = orderBy(this.profilesType1, [
                    (o) => o.name.toLowerCase()
                ]);
                this.profilesType2 = orderBy(this.profilesType2, [
                    (o) => o.name.toLowerCase()
                ]);
                this.apiProfiles = orderBy(this.apiProfiles, [
                    (o) => o.name.toLowerCase()
                ]);
            },

            $_gotoOverview() {
                this.$router.push({ path: '/dashboard/userGroups' });
            },

            $_onBaseLayerSelectSelected(value) {
                this.selectedBaseLayerId = value;
            },

            $_onSubmit() {
                if (this.$refs.userform.checkValidity()) {
                    // Show error when saving a non-admin user for an organization with only a Web license
                    if (
                        !this.userInfo.licenses.has_mobile_license &&
                        this.userInfo.licenses.has_web_license &&
                        !this.record.isAdmin
                    ) {
                        // toaster show error
                        const data = {
                            message: this.$t('userWebLicenseOnlyAdmin'),
                            variant: 'danger',
                            autoHideDelay: 5000,
                            noCloseButton: true
                        };
                        this.showToast(data);
                    } else {
                        this.$_onSave(false);
                    }
                }
            },
            $_toggleUserEnabled(attribute) {
                attribute.userEnabled = !attribute.userEnabled;
            },

            $_onSave: function () {
                // Copy selected items in this.profilesType2 into selectedProfiles
                let selectedProfiles = this.profilesType2.filter((p) => {
                    return p.userEnabled === true;
                });

                // Copy selected items in this.profilesType2 into selectedProfiles
                const selectedApiProfiles = this.apiProfiles.filter((p) => {
                    return p.userEnabled === true;
                });

                selectedProfiles = selectedProfiles.concat(selectedApiProfiles);
                // Append selectedProfiles with the selected ondergrond layer profile
                if (this.selectedBaseLayerId) {
                    const selectedBaseLayer = { id: this.selectedBaseLayerId };
                    selectedProfiles.push(selectedBaseLayer);
                }

                /**
                 * Copy selectedProfiles into the profileUsers
                 */
                this.record.userGroupProfiles = selectedProfiles.map(function (p) {
                    return { profileId: p.id };
                });

                this.isSaving = true;

                /**
                 * Save user
                 */

                const name = this.editMode ? 'UpdateUserGroup' : 'CreateUserGroup';
                this.$store
                    .dispatch('useApiData', {
                        name: name,
                        data: this.record
                    })
                    .then((result) => {
                        const data = {
                            message: this.$t('userGroupSavedConfirmation'),
                            variant: 'success',
                            autoHideDelay: 2500,
                            noCloseButton: true
                        };
                        this.showToast(data);
                        this.isSaving = false;

                        if (!this.editMode) {
                            this.$_gotoOverview();
                        }
                    })
                    .catch((error) => {
                        const data = {
                            message: error.message,
                            variant: 'danger',
                            title: this.$t('saveFailureTitle'),
                            autoHideDelay: 10000
                        };
                        this.showToast(data);
                        this.isSaving = false;
                    });
            },

            async $_onRemove() {
                let msg = this.$t('userGroupDeletionsConfirmation');

                const confirm = await this.msgBoxConfirm({ message: msg });
                if (confirm) {
                    try {
                        await this.$store.dispatch('useApiData', {
                            name: 'DeleteUserGroup',
                            parameters: { Id: this.record.id }
                        });
                        const data = {
                            message: this.$t('messageUserGroupRemoved'),
                            variant: 'success',
                            autoHideDelay: 5000
                        };
                        this.showToast(data);
                        this.$_gotoOverview();
                    } catch (error) {
                        const data = {
                            message: this.$t(error.message),
                            variant: 'danger',
                            title: this.$t(error.title),
                            autoHideDelay: 5000
                        };
                        this.showToast(data);
                        this.isDeleting = false;
                    } finally {
                        this.isDeleting = false;
                    }
                }
            },

            $_onCancel: function () {
                this.$_gotoOverview();
            }
        }
    };
</script>
<style>
.user-enabled {
    width: 70px;
}
</style>
